/**
 * Big bang and join: what to tween when the rings are re-laid out.
 *
 * Changing a node's mode recomputes every placement, and the scene moves each
 * ring from where it was to where it now is over half a second (docs/CIRCLES.md
 * §3). Two cases have no "where it was" to move from: a ring that has just
 * appeared, and one that has just gone. Both are answered by the tree — a new
 * ring emerges from where its parent was, and a departing one collapses into
 * where its parent now is. That is what makes a split look like a big bang and
 * a join look like a homecoming rather than a pop.
 *
 * Pure and frame-free: the plan is computed once per layout change, and the
 * scene interpolates it per frame. Marks and floats follow from the
 * interpolated transform; nothing here knows about either.
 */

import type { RingPlacement, RingTransform } from './rings';

const TAU = Math.PI * 2;

/** One ring's journey. `from === to` means it did not move. */
export interface RingTransition {
  id: string;
  from: RingTransform;
  to: RingTransform;
}

/** The tweenable part of a placement. */
export function transformOf(placement: RingPlacement): RingTransform {
  return {
    cx: placement.cx,
    cy: placement.cy,
    r: placement.r,
    rotation: placement.rotation,
  };
}

/** Shortest signed way round from `from` to `to`, in (-π, π]. */
function rotationDelta(from: number, to: number): number {
  const raw = (to - from) % TAU;
  const forward = raw < 0 ? raw + TAU : raw;
  return forward > Math.PI ? forward - TAU : forward;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Ease in, ease out, cubic — the curve the scene runs its 500 ms on. Clamped at
 * both ends, so a tween that overruns its duration settles instead of carrying
 * on. `prefers-reduced-motion` is the caller's business: it jump-cuts instead.
 */
export function easeInOutCubic(t: number): number {
  if (!Number.isFinite(t) || t <= 0) return 0;
  if (t >= 1) return 1;
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * A transform `t` of the way from `a` to `b`. Straight lines for the centre and
 * the radius; the shortest way round for the rotation, so a ring never spins
 * the long way to reach an angle just behind it.
 *
 * `t` is not clamped — pass it through {@link easeInOutCubic} first, or through
 * whatever curve you like, overshoot included.
 */
export function interpolateTransform(a: RingTransform, b: RingTransform, t: number): RingTransform {
  const k = Number.isFinite(t) ? t : 0;
  return {
    cx: lerp(a.cx, b.cx, k),
    cy: lerp(a.cy, b.cy, k),
    r: lerp(a.r, b.r, k),
    rotation: a.rotation + rotationDelta(a.rotation, b.rotation) * k,
  };
}

/** The nearest ancestor of `id` (itself first) that `have` knows about. */
function nearestKnown(
  id: string,
  chain: ReadonlyMap<string, RingPlacement>,
  have: ReadonlyMap<string, RingPlacement>,
): RingPlacement | undefined {
  let cursor: string | null = id;
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const known = have.get(cursor);
    if (known) return known;
    const node: RingPlacement | undefined = chain.get(cursor);
    if (!node) return undefined;
    cursor = node.parentId;
  }
  return undefined;
}

function index(placements: readonly RingPlacement[]): Map<string, RingPlacement> {
  const out = new Map<string, RingPlacement>();
  for (const p of placements ?? []) {
    if (p && typeof p.id === 'string' && !out.has(p.id)) out.set(p.id, p);
  }
  return out;
}

/**
 * What every ring should do to get from `prev` to `next`.
 *
 * A ring in both tweens between its two transforms. A ring only in `next` was
 * just born: it starts from where its nearest surviving ancestor stood before —
 * its parent, or further up when a whole branch appeared at once — and expands
 * into place. A ring only in `prev` is leaving: it collapses into where its
 * nearest surviving ancestor stands now. With no such ancestor either way (the
 * first layout, or the last) a ring simply stays where it is and the tween is a
 * no-op.
 *
 * The result lists the rings of `next` in their own order, then the departing
 * ones in theirs, so the plan is as deterministic as the layout it came from.
 */
export function ringTransitionPlan(
  prev: readonly RingPlacement[],
  next: readonly RingPlacement[],
): RingTransition[] {
  const before = index(prev);
  const after = index(next);
  const out: RingTransition[] = [];

  for (const placement of next ?? []) {
    if (!placement || typeof placement.id !== 'string') continue;
    if (after.get(placement.id) !== placement) continue; // a duplicate id; first wins
    const to = transformOf(placement);
    const was = before.get(placement.id);
    if (was) {
      out.push({ id: placement.id, from: transformOf(was), to });
      continue;
    }
    // Big bang: out of wherever the parent used to be.
    const ancestor = placement.parentId ? nearestKnown(placement.parentId, after, before) : undefined;
    out.push({ id: placement.id, from: ancestor ? transformOf(ancestor) : to, to });
  }

  for (const placement of prev ?? []) {
    if (!placement || typeof placement.id !== 'string') continue;
    if (before.get(placement.id) !== placement) continue;
    if (after.has(placement.id)) continue;
    const from = transformOf(placement);
    // Join: into wherever the parent has ended up.
    const ancestor = placement.parentId ? nearestKnown(placement.parentId, before, after) : undefined;
    out.push({ id: placement.id, from, to: ancestor ? transformOf(ancestor) : from });
  }

  return out;
}
